import { useRef } from "react";

import { useQuery } from "@tanstack/react-query";

import { getScenarioResilienceScore } from "services";

import { GET_SCENARIO_RESILIENCE_SCORE } from "consts";

import { ScenarioResilienceByScenarioId, ScenarioResilienceInput } from "pages/app/scenario-analysis/utils/scenarioResilienceScore";

type UseGetScenarioResilienceScoreParams = {
  farmId: string | null | undefined;
  fieldId: string | null | undefined;
  projectionYears: number;
  scenarios: ScenarioResilienceInput[];
  enabled?: boolean;
};

export const useGetScenarioResilienceScore = ({ farmId, fieldId, projectionYears, scenarios, enabled = true }: UseGetScenarioResilienceScoreParams) => {
  // Keep volatile params in a ref so query key stays stable across crop-year edits and
  // timeline changes. The query only refetches when explicitly invalidated (e.g. after
  // recalculation) — not on every user edit — so rawResilienceData never drops to {}.
  const paramsRef = useRef({ projectionYears, scenarios });
  paramsRef.current = { projectionYears, scenarios };

  return useQuery<ScenarioResilienceByScenarioId>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- paramsRef is intentionally excluded from the key; volatile params are read via ref so the key stays stable across edits and data only refreshes on explicit invalidation.
    queryKey: [GET_SCENARIO_RESILIENCE_SCORE, farmId, fieldId],
    queryFn: async () => {
      const { projectionYears: py, scenarios: sc } = paramsRef.current;
      const results = await getScenarioResilienceScore({ farmId: farmId!, fieldId, projectionYears: py, scenarios: sc });
      return Object.fromEntries(results.map((r) => [r.scenarioId, r]));
    },
    enabled: enabled && !!farmId && scenarios.length > 0,
  });
};
